import Vue from "vue";
import VueRouter from "vue-router";
import routes from "./routes";
import { supabase } from "@/supabase";
import { leaveAppForWebsite } from "@/utils/auth";
import pinia from "@/stores";
import i18n, { initializeLocale } from "@/i18n";
import { useCurrentUserStore } from "@/stores/currentUser";

Vue.use(VueRouter);

function getSafeAuthRedirectPath(path) {
  if (typeof path !== "string" || !path.startsWith("/")) return "/create-clips";
  if (path === "/login") return "/create-clips";
  return path;
}

function hasOAuthHashFragment() {
  if (typeof window === "undefined") return false;
  const hash = String(window.location.hash || "");
  return (
    hash.includes("access_token=")
    || hash.includes("refresh_token=")
    || hash.includes("error=")
  );
}

// configure router
const router = new VueRouter({
  mode: "history",
  routes, // short for routes: routes
  linkActiveClass: "active",
});

// Route guard to protect authenticated routes
router.beforeEach(async (to, from, next) => {
  const fromPath = String(from?.path || "");
  const toPath = String(to?.path || "");
  const isCreateClipsInternalNavigation =
    fromPath.startsWith("/create-clips") && toPath.startsWith("/create-clips");
  if (isCreateClipsInternalNavigation) {
    next();
    return;
  }

  let session = null;
  let isAuthenticated = false;
  
  try {
    // Add timeout to prevent hanging
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Session check timeout')), 5000)
    );
    
    const { data } = await Promise.race([sessionPromise, timeoutPromise]);
    session = data?.session || null;
    isAuthenticated = !!session;
  } catch (error) {
    console.warn("Session check failed or timed out:", error);
    // Default to not authenticated if check fails
    isAuthenticated = false;
  }

  // Check if route requires authentication
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (requiresAuth && !isAuthenticated) {
    if (hasOAuthHashFragment()) {
      next({
        path: "/login",
        query: { redirect: getSafeAuthRedirectPath(to.fullPath) },
      });
      return;
    }
    leaveAppForWebsite({ locale: i18n.locale });
    next(false);
    return;
  }

  const currentUserStore = useCurrentUserStore(pinia);

  if (requiresAuth && isAuthenticated && session) {
    try {
      await currentUserStore.ensureSessionReady(session, { force: from.path === "/login" });
      initializeLocale(currentUserStore.savedLocale);
    } catch (error) {
      console.error("Error bootstrapping current user:", error);
      leaveAppForWebsite({ locale: i18n.locale });
      next(false);
      return;
    }
  }

  if (to.path === "/login" && isAuthenticated && session && !currentUserStore.profile) {
    try {
      await currentUserStore.ensureSessionReady(session, { force: true });
      initializeLocale(currentUserStore.savedLocale);
    } catch (error) {
      console.error("Error bootstrapping current user for login redirect:", error);
    }
  }

  // Check if route requires admin role
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  const requiresTwitchUser = to.matched.some((record) => record.meta.requiresTwitchUser);
  
  if (requiresAdmin && isAuthenticated && session) {
    try {
      if (!currentUserStore.hasAdminAccess) {
        // Redirect to Clipr base route if not admin
        next({ path: "/create-clips" });
        return;
      }
    } catch (error) {
      console.error("Error checking admin role:", error);
      // If admin check fails, redirect to Clipr base route for safety
      next({ path: "/create-clips" });
      return;
    }
  }

  if (requiresTwitchUser && isAuthenticated && session && !currentUserStore.isTwitchUser) {
    next({ path: "/create-clips" });
    return;
  }

  const requiresNonAdmin = to.matched.some((record) => record.meta.requiresNonAdmin);
  if (requiresNonAdmin && isAuthenticated && session && currentUserStore.hasAdminAccess) {
    next({ path: "/create-clips" });
    return;
  }

  if (isAuthenticated && session && requiresAuth) {
    const isCreateClipsPath = to.path === "/create-clips" || to.path.startsWith("/create-clips/");
    const isAdminResultsPath = to.path.startsWith("/public/results/");
    const twitchUserRoutes = [
      "/update-password",
      "/user-profile",
      "/affiliate-program",
      "/twitch-postadmin", "/results", "/users", "/billing", "/billing/success",
      "/clipr-purchases", "/subscription-plans-admin", "/newsletters", "/welcome", "/studio", "/agenda", "/analisis",
    ];
    if (isCreateClipsPath || isAdminResultsPath || twitchUserRoutes.includes(to.path)) {
      next();
      return;
    }
    next({ path: "/create-clips" });
    return;
  }

  if (to.path === "/login" && isAuthenticated) {
    next({ path: "/create-clips" });
  } else {
    next();
  }
});

export default router;
